import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, PillTag, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, buildSelectFields,
  unwrapLookupItems,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/documentseries';

// Belge Türü: 0=Sipariş, 1=İrsaliye, 2=Fatura (DocumentTemplate.DocType / ResolveData docType ile AYNI)
const DOCUMENT_TYPE_OPTIONS = [
  { value: 0, label: 'Sipariş' },
  { value: 1, label: 'İrsaliye' },
  { value: 2, label: 'Fatura' },
];
// Belge Tipi: 0=Satış, 1=Alış, 2=İade, 3=Hizmet (Invoice.Type semantiğiyle uyumlu)
const DOCUMENT_TIP_OPTIONS = [
  { value: 0, label: 'Satış' },
  { value: 1, label: 'Alış' },
  { value: 2, label: 'İade' },
  { value: 3, label: 'Hizmet' },
];
const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
};

const docTypeLabel = (v) => (DOCUMENT_TYPE_OPTIONS.find((o) => o.value === Number(v)) || {}).label || '-';
const docTipLabel = (v) => (DOCUMENT_TIP_OPTIONS.find((o) => o.value === Number(v)) || {}).label || '-';

const COLUMN_FILTER_META = {
  vendorLabel: { type: 'select', odataField: 'DistributorId', valueFormat: 'guid' },
  year: { type: 'number', odataField: 'Year' },
  documentType: { type: 'select', odataField: 'DocumentType', options: DOCUMENT_TYPE_OPTIONS },
  documentTip: { type: 'select', odataField: 'DocumentTip', options: DOCUMENT_TIP_OPTIONS },
  seriesNo: { type: 'text', odataField: 'SeriesNo' },
};

const SEARCH_FIELDS = ['SeriesNo'];

export function DocumentSeriesPage() {
  usePageMeta('Seri Yönetimi', 'Belge türü ve tipi bazında matbu seri tanımlarını yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [vendors, setVendors] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getVendorLabel = useCallback((id) => {
    const v = vendors.find((item) => item.id === id);
    if (!v) return '-';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : v.textId || '-';
  }, [vendors]);

  const getDesignLabel = useCallback((id) => {
    if (!id) return '-';
    const d = designs.find((item) => item.id === id);
    return d ? (d.name || '-') : '-';
  }, [designs]);

  useEffect(() => {
    Promise.all([
      fetch('/api/vendors/Select', { headers: authHeaders() })
        .then((res) => res.ok ? res.json() : [])
        .then((data) => setVendors(unwrapLookupItems(data)))
        .catch(() => {}),
      fetch('/api/documenttemplates', { headers: authHeaders() })
        .then((res) => res.ok ? res.json() : [])
        .then((data) => setDesigns(unwrapLookupItems(data)))
        .catch(() => {}),
    ]).finally(() => setLookupsLoaded(true));
  }, []);

  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    const key = (field === 'documentType' || field === 'documentTip' || field === 'year')
      ? (rawKey === '__none__' ? '__none__' : Number(rawKey)) : rawKey;
    let label;
    if (key === '__none__') label = 'Atanmamış';
    else if (field === 'distributorId') label = getVendorLabel(key);
    else if (field === 'documentType') label = docTypeLabel(key);
    else if (field === 'documentTip') label = docTipLabel(key);
    else label = String(key);
    return { key, label };
  }, [getVendorLabel]);

  // $select: görünür kolonların OData alanları + kritik alanlar. lastNumber/designLabel
  // (DesignId)/isActive görünür kolon ama COLUMN_FILTER_META'da yok → always'e eklendi;
  // Id satır anahtarı/nav. sortField $select'e katılmaz ($orderby tam entity'ye uygulanır).
  // Grup görünümü needsFullData → hook içinde $select yok sayılır.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'LastNumber', 'DesignId', 'IsActive'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'UpdatedDate',
    defaultSortDir: 'desc',
    getKeyLabel,
    ready: lookupsLoaded,
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    nestedGroupBuckets, fetchData, exportRows,
  } = list;

  // Ana kayıt ekle/görüntüle/düzenle artık tam-sayfa detaya (/document-series/:id) gider.
  const openNew = () => navigate('/document-series/new');
  const openDetail = (row) => { if (!row) return; navigate(`/document-series/${row.id}`, { state: { row } }); };
  const openEditRow = (row) => { if (!row) return; navigate(`/document-series/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Seri başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor((row) => getVendorLabel(row.distributorId), {
      id: 'vendorLabel', header: 'Distribütör', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor('year', { header: 'Yıl', meta: { filterType: 'number' }, cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('documentType', {
      header: 'Belge Türü', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return v == null ? '-' : <PillTag tone="info">{docTypeLabel(v)}</PillTag>; },
    }),
    columnHelper.accessor('documentTip', {
      header: 'Belge Tipi', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return v == null ? '-' : docTipLabel(v); },
    }),
    columnHelper.accessor('seriesNo', {
      header: 'Seri No', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('lastNumber', {
      header: 'Son No', meta: { filterType: 'number' },
      cell: (info) => { const v = info.getValue(); return v != null ? v : '-'; },
    }),
    columnHelper.accessor((row) => getDesignLabel(row.designId), {
      id: 'designLabel', header: 'Dizayn', enableSorting: false,
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : v; },
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', enableSorting: false,
      cell: (info) => {
        const meta = info.getValue() === false ? STATUS_META[0] : STATUS_META[1];
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getVendorLabel, getDesignLabel, canEdit, canDelete]);

  const lookupOptions = useMemo(() => ({
    vendorLabel: vendors.map((v) => ({ value: v.id, label: v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || v.id) })),
  }), [vendors]);

  const resolveFilterProps = useCallback((columnId, meta) => ({ options: meta.options || lookupOptions[columnId] }), [lookupOptions]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Distribütör', valueGetter: ({ row }) => getVendorLabel(row.distributorId) },
        { field: 'year', headerName: 'Yıl' },
        { headerName: 'Belge Türü', valueGetter: ({ row }) => docTypeLabel(row.documentType) },
        { headerName: 'Belge Tipi', valueGetter: ({ row }) => docTipLabel(row.documentTip) },
        { field: 'seriesNo', headerName: 'Seri No' },
        { headerName: 'Dizayn', valueGetter: ({ row }) => getDesignLabel(row.designId) },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive === false ? 'Pasif' : 'Aktif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `seri_yonetimi_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Seri No ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Seri Yönetimi"
        subtitle="Belge türü ve tipi bazında matbu seri tanımlarını filtreleyin, sıralayın ve yönetin."
        actions={canAdd ? (
          <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Seri</button>
        ) : null}
        toolbar={toolbarNode}
        footer={isGrouping ? null : footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          resolveFilterProps={resolveFilterProps}
          isGrouping={isGrouping}
          nestedGroupBuckets={nestedGroupBuckets}
          expandedGroups={expandedGroups}
          toggleGroupExpand={toggleGroupExpand}
          bucketCounts={bucketCounts}
          bucketLoadingByPath={bucketLoadingByPath}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.seriesNo}"</strong> serisini silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
